import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HoverCard } from 'radix-ui';
import { cn } from '@/lib/utils';

export interface FlyoutSubItem {
  name: string;
  href: string;
  icon: React.ElementType;
  isActive?: boolean;
  badge?: React.ReactNode;
}

interface SidebarFlyoutProps {
  title: string;
  icon: React.ElementType;
  headerBadge?: React.ReactNode;
  isActive?: boolean;
  triggerBadge?: React.ReactNode;
  items: FlyoutSubItem[];
  onTriggerClick?: () => void;
  triggerClassName?: string;
  iconClassName?: string;
}

export function SidebarFlyout({
  title,
  icon: Icon,
  headerBadge,
  isActive = false,
  triggerBadge,
  items,
  onTriggerClick,
  triggerClassName,
  iconClassName = 'h-5 w-5 text-primary',
}: SidebarFlyoutProps) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close flyout immediately whenever the route changes
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  return (
    <HoverCard.Root open={open} onOpenChange={setOpen} openDelay={80} closeDelay={150}>
      <HoverCard.Trigger asChild>
        <button
          type="button"
          onClick={onTriggerClick}
          className={triggerClassName}
          aria-label={title}
        >
          <Icon className={iconClassName} strokeWidth={isActive ? 2.4 : 2} />
          {triggerBadge}
        </button>
      </HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content
          side="right"
          align="start"
          sideOffset={12}
          collisionPadding={16}
          avoidCollisions
          className={cn(
            'z-50 min-w-[210px] max-w-[280px] rounded-xl border border-sidebar-border bg-sidebar/95 p-1.5 shadow-2xl backdrop-blur-md',
            'text-sidebar-foreground',
            'animate-in fade-in-0 zoom-in-95 data-[side=right]:slide-in-from-left-2 duration-150',
            'focus:outline-none'
          )}
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-2.5 py-2 border-b border-sidebar-border/60 mb-1">
            <Icon className="h-4 w-4 text-primary shrink-0" strokeWidth={2.2} />
            <span className="font-semibold text-xs tracking-tight text-sidebar-foreground truncate">
              {title}
            </span>
            {headerBadge && <div className="ml-auto shrink-0">{headerBadge}</div>}
          </div>

          {/* Sub-items */}
          <div className="flex flex-col gap-0.5 max-h-[min(380px,75vh)] overflow-y-auto hide-scrollbar">
            {items.map((item) => {
              const SubIcon = item.icon;
              return (
                <Link
                  key={item.href || item.name}
                  to={item.href}
                  onClick={() => setOpen(false)}
                  className={cn(
                    'flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-150',
                    item.isActive
                      ? 'bg-primary/15 text-primary font-semibold shadow-xs'
                      : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                  )}
                  title={item.name}
                >
                  <SubIcon
                    className="h-4 w-4 shrink-0 text-primary"
                    strokeWidth={item.isActive ? 2.4 : 2}
                  />
                  <span className="truncate flex-1">{item.name}</span>
                  {item.badge}
                </Link>
              );
            })}
          </div>
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}
